import { Table, notification } from "antd";
import React, { FC, useState } from "react";
import {
  ReportItem,
  useDeleteReportMutation,
  useGetReportsQuery,
} from "shared/api";
import {
  useConfirmDeleteModal,
  useDebouncedTableFilter,
  usePagination,
  useTableConfigColumns,
} from "shared/hooks";
import { handleRequestError } from "shared/helpers";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { renderPaginationTotal } from "shared/ui";

interface TemplatesConfigTableProps {
  showEditModal: () => void;
}

const TemplatesConfigTable: FC<TemplatesConfigTableProps> = ({
  showEditModal,
}) => {
  const { setValues } = useFormikContext<ReportItem>();

  const { t } = useTranslation();

  const [api, contextHolder] = notification.useNotification();

  const pagination = usePagination(20);

  const [ordering, setOrdering] = useState<string>("");

  const changeSort = (ascendOrdering: string, descendOrdering: string) => {
    setOrdering(ordering === ascendOrdering ? descendOrdering : ascendOrdering);
  };

  const { pageSize, currentPage, setCurrentPage, setPageSize, offset, limit } =
    pagination;

  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const [selectedReportTypes, setSelectedReportTypes] = useState<string[]>([]);
  const [selectedTemplateHtmlValues, setSelectedTemplateHtmlValues] = useState<
    string[]
  >([]);

  const {
    debouncedValue: debouncedTemplateIdValue,
    handleFilterChange: handleTemplateIdChange,
    value: templateIdValue,
    clearFilterValue: clearTemplateIdValue,
  } = useDebouncedTableFilter("templateId", 400);

  const {
    debouncedValue: debouncedCategoryValue,
    handleFilterChange: handleCategoryChange,
    value: categoryValue,
    clearFilterValue: clearCategoryValue,
  } = useDebouncedTableFilter("reportCategory", 400);

  const [isDefaultValue, setIsDefaultValue] = useState<boolean | null>(null);

  const {
    data: reports,
    isFetching,
    isLoading,
  } = useGetReportsQuery({
    offset,
    limit,
    name__in: selectedNames.length ? selectedNames : undefined,
    template_id__icontains: debouncedTemplateIdValue || undefined,
    template_html__in: selectedTemplateHtmlValues.length
      ? selectedTemplateHtmlValues
      : undefined,
    report_type__in: selectedReportTypes.length
      ? selectedReportTypes
      : undefined,
    report_category__name__icontains: debouncedCategoryValue || undefined,
    ...(isDefaultValue !== null && { is_default: isDefaultValue }),
    ...(ordering && { ordering }),
    expand: "report_category",
  });

  const { data: allReports } = useGetReportsQuery({});
  const nameOptions = Array.isArray(allReports)
    ? allReports.map((r: ReportItem) => ({
        label: r.name,
        value: r.name,
      }))
    : [];

  const [deleteReport] = useDeleteReportMutation();

  const handleDeleteReport = async (report: ReportItem) => {
    try {
      await deleteReport(report).unwrap();
      api.success({
        message: t("reports.success-title"),
        description: t("reports.success-delete-msg"),
      });
    } catch (error) {
      api.error({
        message: t("reports.error-title"),
        description: handleRequestError(error, t("reports.error")),
      });
    }
  };

  const showDeleteReportModal = useConfirmDeleteModal(
    t("reports.delete-alert-msg"),
    handleDeleteReport
  );

  const handleEditReport = (record: ReportItem) => {
    showEditModal();
    setValues(record);
  };

  const columns = useTableConfigColumns({
    t,
    handleEditReport,
    showDeleteReportModal,
    selectedNames,
    nameOptions,
    setSelectedNames,
    templateIdValue,
    handleTemplateIdChange,
    clearTemplateIdValue,
    selectedReportTypes,
    setSelectedReportTypes,
    reportCategoryValue: categoryValue,
    handleReportCategoryChange: handleCategoryChange,
    clearReportCategoryValue: clearCategoryValue,
    isDefaultValue,
    setIsDefaultValue,
    selectedTemplateHtmlValues,
    setSelectedTemplateHtmlValues,
    changeSort,
  });

  return (
    <>
      {contextHolder}

      <Table
        loading={isFetching || isLoading}
        columns={columns}
        dataSource={reports?.results}
        rowKey={(obj) => obj.id!}
        pagination={{
          pageSize,
          showSizeChanger: true,
          current: currentPage,
          onShowSizeChange(page, pageSize) {
            setPageSize(pageSize);
            setCurrentPage(page);
          },
          onChange(page) {
            setCurrentPage(page);
          },
          total: reports?.count,
          showTotal: renderPaginationTotal,
        }}
      />
    </>
  );
};

export { TemplatesConfigTable };
